import FacebookMessenger from '@/components/FacebookMessenger'
import FormButton from '@/components/Form/FormButton'
import FormGroup from '@/components/Form/FormGroup'
import FormInput from '@/components/Form/FormInput'
import FormText from '@/components/Form/FormText'
import FormTextRequire from '@/components/Form/FormTextRequire'
import FormView from '@/components/Form/FormView'
import { SearchOrderIcon } from '@/components/Icon/SearchOrderIcon'
import { SideBarBanner } from '@/components/SideBarBanner'
import { useAuth } from '@/containers'
import { useCookie } from '@/hooks/useCookie'
import HeaderNoMenu from '@/layouts/HeaderNoMenu'
import { api } from '@/utils'
import { setContext } from '@/utils/api-interceptors'
import { translateSchema } from '@/utils/validations'
import clsx from 'clsx'
import { deleteCookie, getCookies, setCookie } from 'cookies-next'
import { useFormik } from 'formik'
import { type GetServerSideProps } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

type Props = {
  phoneNumber: string | null
}

const SearchOrder = (props: Props) => {
  const t = useTranslations('searchOrder')
  const [phoneNumber, setPhoneNumber] = useCookie<string | null>(
    'phoneNumber',
    props.phoneNumber || null
  )
  const [requestId, setRequestId] = useCookie('requestId', '')
  const [remainTime, setRemainTime] = useState(120)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { token, setUser } = useAuth()
  const router = useRouter()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timeRef = useRef<any>(null)

  const onSubmitFn = () => {
    setIsSubmitted(true)
  }

  useEffect(() => {
    return () => {
      deleteCookie('requestId')
      deleteCookie('phoneNumber')
    }
  }, [])

  const formik = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: translateSchema('searchOrder', t),

    onSubmit: async values => {
      setErrorMessage('')
      setIsProcessing(true)

      // reCAPTCHA: lấy token theo Defensive Execution Pattern
      const captchaKey = process.env.NEXT_PUBLIC_CAPTCHA_KEY
      let tokenReCaptcha: string | undefined
      if (captchaKey && window.grecaptcha) {
        try {
          tokenReCaptcha = await window.grecaptcha.execute(captchaKey, {
            action: 'login',
          })
        } catch (e) {
          console.warn('reCAPTCHA execute failed', e)
        }
      }

      const res = await api(`api/auth/login/request-otp`, {
        method: 'post',
        baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
        ...(tokenReCaptcha ? { headers: { recaptcha: tokenReCaptcha } } : {}),
        data: values,
      })
      if (res instanceof Error) {
        setErrorMessage(res.message)
        setIsProcessing(false)
        return
      }
      setPhoneNumber(values.phone)
      setIsProcessing(false)
      setRequestId(res.data.requestId)
    },
  })

  const formikOtp = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: translateSchema('searchOrderOtp', t),

    onSubmit: async values => {
      setIsProcessing(true)
      const res = await api(`api/auth/login/otp`, {
        method: 'post',
        baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
        data: {
          code: values.otp.trim(),
          requestId,
        },
      })
      if (res instanceof Error) {
        setIsProcessing(false)
        setErrorMessage(res.message)
        return
      }
      setIsProcessing(false)
      setUser(res.data.profile)
      setCookie('token', res.data.token, { maxAge: 2147483647 })
      setCookie('channel', res.data.profile.saleChannel, { maxAge: 2147483647 })
      deleteCookie('phoneNumber')
      deleteCookie('requestId')
      router.push('/orders')
    },
  })

  const handleResendOtp = async () => {
    setErrorMessage('')

    // reCAPTCHA: lấy token theo Defensive Execution Pattern
    const captchaKey = process.env.NEXT_PUBLIC_CAPTCHA_KEY
    let tokenReCaptcha: string | undefined
    if (captchaKey && window.grecaptcha) {
      try {
        tokenReCaptcha = await window.grecaptcha.execute(captchaKey, {
          action: 'login',
        })
      } catch (e) {
        console.warn('reCAPTCHA execute failed', e)
      }
    }

    api(`api/auth/login/request-otp`, {
      method: 'post',
      baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
      ...(tokenReCaptcha ? { headers: { recaptcha: tokenReCaptcha } } : {}),
      data: { phone: phoneNumber },
    })
      .then(() => {
        clearInterval(timeRef.current)
        setRemainTime(120)
        timeRef.current = setInterval(() => {
          setRemainTime(prevTime => prevTime - 1)
        }, 1000)
      })
      .catch(error => {
        setErrorMessage(error.message)
      })
  }

  useEffect(() => {
    timeRef.current = setInterval(() => {
      setRemainTime(prev => prev - 1)
    }, 1000)
    return () => {
      clearInterval(timeRef.current)
    }
  }, [])

  useEffect(() => {
    if (remainTime === 0) {
      clearInterval(timeRef.current)
    }
  }, [remainTime])

  if (token) {
    return router.push('/orders')
  }

  return (
    <>
      <style global jsx>{`
        body::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <HeaderNoMenu />
      <main>
        {' '}
        <FacebookMessenger />
        <SideBarBanner className='top-[37%]'></SideBarBanner>
        <div className='h-[calc(100vh - 129px)] no-scrollbar m-auto flex max-w-[1200px] items-center gap-x-[210px] pt-[5px]'>
          <div className='flex w-full flex-2 flex-col'>
            <div className='text-titleSection text-dark-400'>
              {t('searchOrder')}
            </div>
            <div className='mt-[40px]'>
              {phoneNumber && phoneNumber !== '' ? (
                <>
                  <div>
                    <div className=''>
                      <FormView
                        onSubmit={formikOtp.handleSubmit}
                        buttonClassName='mt-1'
                      >
                        <FormGroup className='flex w-full flex-col'>
                          <FormText className='mb-[32px] text-left text-Headline font-normal text-dark-800'>
                            {t('otpSent')}{' '}
                            <span className='font-bold'>{phoneNumber}</span>
                          </FormText>
                          <FormInput
                            name='otp'
                            placeholder={t('otpPld')}
                            value={formikOtp.values.otp.trim()}
                            isError={isSubmitted && formikOtp.errors.otp}
                            onChange={e => {
                              formikOtp.handleChange({
                                target: {
                                  value: e.target.value.trim(),
                                  name: 'otp',
                                },
                              })
                              setErrorMessage('')
                            }}
                            alert
                            className={clsx(
                              formikOtp.errors.otp &&
                                'border border-primary-second',
                              '!h-12 w-[380px] min-w-[360px] rounded-lg border border-solid border-dark-200 text-left transition  placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                          ></FormInput>
                          {isSubmitted && formikOtp.errors.otp ? (
                            <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-primary-second'>
                              {formikOtp.errors.otp}
                            </FormTextRequire>
                          ) : (
                            ''
                          )}
                          {errorMessage && isSubmitted ? (
                            <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-primary-second'>
                              {errorMessage}
                            </FormTextRequire>
                          ) : (
                            <></>
                          )}
                        </FormGroup>
                        <FormButton
                          className={clsx(
                            formikOtp.dirty &&
                              ' bg-primary-base text-base text-white',
                            'disabled:text-[rgb(0 0 0 / 25%)] h-11 w-full rounded-lg bg-gray-300 font-semibold uppercase text-black-400/25 disabled:bg-gray-300 disabled:!text-black-400/50 disabled:outline-none'
                          )}
                          onClick={onSubmitFn}
                          isProcessing={isProcessing}
                          disabled={!formikOtp.dirty}
                        >
                          {t('verify')}
                        </FormButton>
                      </FormView>
                    </div>
                    {remainTime === 0 ? (
                      <span className='mt-[32px] block text-center text-fieldLabel'>
                        {' '}
                        <span className=' text-dark-400'>
                          {' '}
                          {t('dontReceive')},{' '}
                        </span>
                        <span
                          className='cursor-pointer text-fieldLabel text-blue'
                          onClick={handleResendOtp}
                        >
                          {t('resend')}
                        </span>
                      </span>
                    ) : (
                      <div className='mt-[32px] text-center'>
                        {t('resendDesc')}{' '}
                        <span>
                          {remainTime} {t('second')}
                        </span>
                      </div>
                    )}
                    <span
                      onClick={() => {
                        setPhoneNumber('')
                        clearInterval(timeRef.current)
                        setRemainTime(120)
                        timeRef.current = setInterval(() => {
                          setRemainTime(prevTime => prevTime - 1)
                        }, 1000)
                      }}
                      className='mt-[24px] block cursor-pointer text-center text-fieldLabel font-normal text-blue'
                    >
                      {t('changePhone')}
                    </span>
                  </div>
                </>
              ) : (
                <FormView
                  onSubmit={formik.handleSubmit}
                  buttonClassName='mt-[24px]'
                >
                  <FormGroup className='flex w-full flex-col'>
                    <FormText className='mb-1 text-left text-fieldInput font-bold'>
                      {t('phone')}
                    </FormText>
                    <FormInput
                      name='phone'
                      placeholder={t('phonePld')}
                      value={formik.values.phone.trim()}
                      isError={isSubmitted && formik.errors.phone}
                      onChange={e => {
                        formik.handleChange({
                          target: {
                            value: e.target.value.trim().replace(/\D/, ''),
                            name: 'phone',
                          },
                        })
                        setErrorMessage('')
                      }}
                      alert
                      className={clsx(
                        isSubmitted &&
                          formik.errors.phone &&
                          'border border-primary-second',
                        ' !h-12 w-[380px] min-w-[360px] rounded border border-solid border-dark-600 text-left text-fieldInput transition placeholder:text-dark-400/70  placeholder:opacity-70 hover:cursor-pointer'
                      )}
                    ></FormInput>
                    {isSubmitted && formik.errors.phone ? (
                      <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-primary-second'>
                        {formik.errors.phone}
                      </FormTextRequire>
                    ) : (
                      ''
                    )}
                    {errorMessage && isSubmitted ? (
                      <FormTextRequire className='border-none pl-0 pt-2 text-fieldInput text-sm  text-primary-second'>
                        {errorMessage}
                      </FormTextRequire>
                    ) : (
                      <></>
                    )}
                  </FormGroup>
                  <FormButton
                    className={clsx(
                      formik.dirty && ' bg-primary-base text-base text-white',
                      'disabled:text-[rgb(0 0 0 / 25%)] h-11 w-full rounded-lg bg-gray-300 font-semibold uppercase text-black-400/25 disabled:bg-gray-300 disabled:!text-black-400/50 disabled:outline-none'
                    )}
                    onClick={onSubmitFn}
                    isProcessing={isProcessing}
                    disabled={isProcessing}
                  >
                    {t('continue')}
                  </FormButton>
                </FormView>
              )}
            </div>
          </div>
          <div className='my-auto ml-auto min-h-[700px] min-w-[538px] py-[60px]'>
            <SearchOrderIcon />
          </div>
        </div>
      </main>
    </>
  )
}

export default SearchOrder

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale,
}) => {
  const { token, phoneNumber, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: locale,
    }
  )

  if (token) {
    return {
      redirect: {
        permanent: false,
        destination: `/orders`,
      },
    }
  }

  return {
    props: {
      messages,
      language,
      phoneNumber: phoneNumber || null,
      token: token || null,
    },
  }
}
